import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MapPin, Wallet, CreditCard, Loader2, CheckCircle2, AlertTriangle, Tag, X, Sparkles, Bike, Star, Heart, Store, Truck, Navigation } from 'lucide-react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { paymentsApi, addressesApi } from '../services/api';
import api from '../services/api';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ShippingSizePicker from '../components/ShippingSizePicker';
import SavedAddressPicker, { SavedAddressSaveButton } from '../components/SavedAddressPicker';
import DriverPreferenceSelector from '../components/DriverPreferenceSelector';
import DeliveryFeeBreakdown from '../components/DeliveryFeeBreakdown';
import { toast } from 'sonner';

const Checkout = () => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { cart, cartTotal, clearCart, backupCart, format: formatBase, placeOrder, currency, delivery, setDelivery, fulfillment, setFulfillment } = useApp();
  // All checkout amounts are in the vendor's currency (CAD in Canada, NGN in Nigeria).
  const format = (v) => formatBase(v, vendor?.currency || cart?.vendorCurrency);
  const { user } = useAuth();
  const [address, setAddress] = useState('');
  const [destLat, setDestLat] = useState(null);
  const [destLng, setDestLng] = useState(null);
  // Which saved-address row (if any) the customer chose. `null` means they're
  // typing a fresh address in the AddressAutocomplete field below.
  const [pickedAddressId, setPickedAddressId] = useState(null);
  const [usingLocation, setUsingLocation] = useState(false);
  const [usedLocation, setUsedLocation] = useState(false); // toggled once GPS ran
  const [savedFromLocation, setSavedFromLocation] = useState(false);
  // Customer's driver-matching preference: auto (default) | nearest | best | super.
  const [driverPreference, setDriverPreference] = useState('auto');

  // Auto-select the user's default saved address on first render so the "Place
  // order" button isn't stuck disabled for someone who already stored a Home
  // address. If the AppContext already has a `delivery.address`, we fall back
  // to that (returning customer with an in-progress session).
  useEffect(() => {
    let alive = true;
    (async () => {
      // 1) Session-hydrated delivery destination wins.
      if (delivery?.address && delivery?.lat != null && delivery?.lng != null) {
        if (!alive) return;
        setAddress(delivery.address);
        setDestLat(delivery.lat);
        setDestLng(delivery.lng);
        return;
      }
      // 2) Otherwise pick the default saved address, if any.
      try {
        const rows = await addressesApi.list();
        const def = (rows || []).find((a) => a.is_default) || rows?.[0];
        if (alive && def) {
          setAddress(def.address);
          setDestLat(def.lat);
          setDestLng(def.lng);
          setPickedAddressId(def.id);
        }
      } catch (_e) { /* not signed in yet or no saved addresses — leave blank */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [outOfArea, setOutOfArea] = useState(false);
  const [dropoffPref, setDropoffPref] = useState('leave_at_door');
  const [note, setNote] = useState('');
  const [placing, setPlacing] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [taxRule, setTaxRule] = useState(null);
  const [nearbyDrivers, setNearbyDrivers] = useState([]);
  const [preferredDriverId, setPreferredDriverId] = useState(null);
  // "Combine my orders" nearby-order nudge: fetched whenever the dest changes.
  const [combineCandidates, setCombineCandidates] = useState(null); // { count, discount_pct, closest_km, message }
  const [combineOptIn, setCombineOptIn] = useState(false);
  // Wallet balance + opt-in to spend it on the delivery fee.
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  // Per-invoice payment method (inline picker on each of the two payment cards).
  //   goodsPayMethod:   how the customer pays the vendor for COGS (subtotal + tax).
  //   fulfillPayMethod: how the customer pays ShipitNow for delivery + service.
  const [goodsPayMethod, setGoodsPayMethod] = useState('card');
  const [fulfillPayMethod, setFulfillPayMethod] = useState('card');
  // PAY FOR ME — one payment (COGS + delivery + service) straight to ShipitNow;
  // a ShipitNow Super Driver pays the store in person at pickup.
  const [payForMe, setPayForMe] = useState(false);
  const [payForMeMethod, setPayForMeMethod] = useState('card');
  const manualSizeRef = useRef(false);
  const [aiSizeSource, setAiSizeSource] = useState(null);
  // When e-transfer is selected, capture confirmation reference + screenshot.
  // These are auto-submitted (via /api/orders/{id}/etransfer/submit) right
  // after the order is placed, so the vendor / admin can review + one-tap
  // approve from their respective portals.
  const [goodsEtRef, setGoodsEtRef] = useState('');
  const [goodsEtFile, setGoodsEtFile] = useState(null);
  const [fulfillEtRef, setFulfillEtRef] = useState('');
  const [fulfillEtFile, setFulfillEtFile] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/wallet/me').then((r) => { if (alive) setWalletBalance(Number(r.data?.balance || 0)); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Fetch both Interac e-transfer recipient emails (vendor + platform) so
  // the checkout's Bank Details card can show customers exactly where to
  // send the transfer.
  const [interacEmails, setInteracEmails] = useState({ vendor: null, platform: null });
  useEffect(() => {
    if (!cart?.vendorId) return;
    let alive = true;
    api.get(`/vendors/${cart.vendorId}/interac`)
      .then((r) => { if (alive) setInteracEmails({
        vendor: r.data?.vendor_interac_email || null,
        platform: r.data?.platform_interac_email || null,
      }); })
      .catch(() => { if (alive) setInteracEmails({ vendor: null, platform: null }); });
    return () => { alive = false; };
  }, [cart?.vendorId]);
  // Vendor gift-pool balance for the current storefront (public endpoint).
  // Customers can redeem this against the goods invoice at checkout.
  const [vendorGiftBalance, setVendorGiftBalance] = useState(0);
  const [useVendorGift, setUseVendorGift] = useState(false);
  useEffect(() => {
    if (!cart?.vendorId) return;
    let alive = true;
    api.get(`/vendors/${cart.vendorId}/gift-pool`)
      .then((r) => { if (alive) setVendorGiftBalance(Number(r.data?.balance_cad || 0)); })
      .catch(() => { if (alive) setVendorGiftBalance(0); });
    return () => { alive = false; };
  }, [cart?.vendorId]);
  // Parcel-size ladder (loaded once from public endpoint). Only shown when the
  // vendor's category is in the admin-configured shipping_size_categories list.
  const [shippingCfg, setShippingCfg] = useState(null); // {sizes, categories}
  const [shippingSizeId, setShippingSizeId] = useState(null);
  // For courier / same-day / logistics vendors, the shopper enters their OWN
  // pickup address. Distance & fee are computed from pickup → destination.
  const [pickupAddress, setPickupAddress] = useState('');
  const [pickupLat, setPickupLat] = useState(null);
  const [pickupLng, setPickupLng] = useState(null);
  const [priority, setPriority] = useState(false);
  // Live quote result populated by clicking "Get quote".
  const [quote, setQuote] = useState(null); // { delivery_fee, distance_km, currency, launch_discount, ... }
  const [quoting, setQuoting] = useState(false);
  // Platform category config (priority multiplier + launch-discount info).
  const [categoryCfg, setCategoryCfg] = useState(null);
  // pickup vs delivery — initialised from the global preference set on Home,
  // and any change here writes back so the choice persists across pages.
  const [fulfillmentType, setFulfillmentType] = useState(fulfillment || 'delivery');
  const isPickup = fulfillmentType === 'pickup';
  // Marketplace 'pay the seller directly' mode — goods settle offline; ShipitNow
  // only charges delivery + service. Goods payment UI/validation is skipped.
  const isMarketplaceDirect = cart.marketplaceMode === 'delivery_only';
  useEffect(() => { if (fulfillmentType !== fulfillment) setFulfillment(fulfillmentType); /* eslint-disable-next-line */ }, [fulfillmentType]);
  // Vendor pickup info (resolved when the customer picks Pickup so we can show
  // the merchant's street address + map).
  const [vendor, setVendor] = useState(null);

  // Fetch vendor details so we can show its pickup address when needed.
  useEffect(() => {
    if (!cart.vendorId) return;
    let alive = true;
    api.get(`/vendors/${cart.vendorId}`)
      .then((r) => { if (alive) setVendor(r.data || null); })
      .catch(() => { if (alive) setVendor(null); });
    return () => { alive = false; };
  }, [cart.vendorId]);

  // Load drivers near the vendor so the customer can manually pick one.
  useEffect(() => {
    if (!cart.vendorId) return;
    let alive = true;
    api.get(`/orders/drivers/nearby`, { params: { vendor_id: cart.vendorId, limit: 8 } })
      .then((r) => { if (alive) setNearbyDrivers(r.data?.drivers || []); })
      .catch(() => { if (alive) setNearbyDrivers([]); });
    return () => { alive = false; };
  }, [cart.vendorId]);

  // Load the shipping-size ladder + applicable vendor categories once.
  useEffect(() => {
    let alive = true;
    api.get('/shipping-sizes')
      .then((r) => { if (alive) setShippingCfg(r.data || null); })
      .catch(() => { if (alive) setShippingCfg(null); });
    return () => { alive = false; };
  }, []);

  // Look up nearby active orders at this vendor so we can nudge "combine yours".
  useEffect(() => {
    if (isPickup || !cart.vendorId || destLat == null || destLng == null) {
      setCombineCandidates(null);
      setCombineOptIn(false);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      api.get('/orders/combine-candidates', {
        params: { vendor_id: cart.vendorId, lat: destLat, lng: destLng, radius_km: 2 },
      })
        .then((r) => { if (alive) setCombineCandidates(r.data || null); })
        .catch(() => { if (alive) setCombineCandidates(null); });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [cart.vendorId, destLat, destLng, isPickup]);

  // Auto-quote — silently fetch the delivery-fee breakdown (km, base, per-km)
  // whenever the destination is known, so every delivery shows its breakdown
  // without requiring the customer to tap "Get quote".
  useEffect(() => {
    if (isPickup || !cart.vendorId || destLat == null || destLng == null) return;
    let alive = true;
    const t = setTimeout(() => {
      api.post('/orders/quote', {
        vendor_id: cart.vendorId,
        dest_lat: destLat,
        dest_lng: destLng,
        shipping_size_id: shippingSizeId || undefined,
        priority,
      })
        .then((r) => { if (alive) setQuote(r.data); })
        .catch(() => {});
    }, 500);
    return () => { alive = false; clearTimeout(t); };
  }, [cart.vendorId, destLat, destLng, isPickup, shippingSizeId, priority]);

  // AI box-size suggestion — food leans small/medium; groceries, packages,
  // courier and logistics baskets are classified from the item types.
  useEffect(() => {
    if (!cart.vendorId || !cart.items?.length || manualSizeRef.current) return;
    let alive = true;
    const t = setTimeout(() => {
      api.post('/orders/suggest-size', {
        vendor_id: cart.vendorId,
        items: cart.items.map((i) => ({ name: i.name, qty: i.qty })),
      })
        .then((r) => {
          if (!alive || manualSizeRef.current || !r.data?.size_id) return;
          setShippingSizeId(r.data.size_id);
          setAiSizeSource(r.data.source);
        })
        .catch(() => {});
    }, 400);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.vendorId, JSON.stringify((cart.items || []).map((i) => [i.id, i.qty]))]);

  // Load category pricing config (priority multiplier + launch-discount info).
  useEffect(() => {
    let alive = true;
    api.get('/category-pricing')
      .then((r) => { if (alive) setCategoryCfg(r.data || null); })
      .catch(() => { if (alive) setCategoryCfg(null); });
    return () => { alive = false; };
  }, []);

  const toggleFavorite = async (driverId) => {
    try {
      const { data } = await api.post('/orders/drivers/favorites/toggle', { driver_id: driverId });
      setNearbyDrivers((arr) => arr.map((d) => d.driver_id === driverId
        ? { ...d, is_favorite: data.favorite_drivers.includes(driverId) }
        : d));
    } catch {
      toast.error('Could not update favorites');
    }
  };

  // Fetch the vendor's tax rule once we know the vendor.
  useEffect(() => {
    if (!cart.vendorId) return;
    let alive = true;
    api.get(`/vendors/${cart.vendorId}/tax-rule`)
      .then((r) => { if (alive) setTaxRule(r.data?.rule || null); })
      .catch(() => { if (alive) setTaxRule(null); });
    return () => { alive = false; };
  }, [cart.vendorId]);

  // WELCOME10 auto-suggest removed — all promo codes are entered manually.

  const shippingApplies = !isPickup && vendor && shippingCfg
    && Array.isArray(shippingCfg.sizes) && shippingCfg.sizes.length > 0
    && (shippingCfg.categories || []).map((c) => String(c).toLowerCase())
       .includes(String(vendor.category || '').toLowerCase());
  const vendorCategory = String(vendor?.category || '').toLowerCase();
  const isLogisticsVendor = ['courier', 'package', 'logistics'].includes(vendorCategory);
  // Auto-selected shipping size id from the platform's category config (e.g. food → medium-box).
  const autoSizeId = (categoryCfg?.category_config || {})[vendorCategory]?.auto_size_id || null;
  const effectiveSizeId = shippingSizeId || autoSizeId;
  // `selectedSize` is used both by the picker (visible only when shippingApplies)
  // AND by the launch-discount banner (must render for retail categories that
  // have an auto_size_id but aren't in the picker's category list).
  const selectedSize = !isPickup && shippingCfg?.sizes
    ? (shippingCfg.sizes.find((s) => s.id === effectiveSizeId) || null)
    : null;
  const launchDiscount = categoryCfg?.category_config?.[vendorCategory];
  const launchActive = launchDiscount?.discount_active
    && (!launchDiscount.discount_expires_at || new Date(launchDiscount.discount_expires_at) > new Date());
  const baseDeliveryFee = isPickup ? 0 : (cart.items.length ? 2.99 : 0);
  const shippingMultiplier = selectedSize ? Number(selectedSize.price_multiplier || 1) : 1;
  // Prefer the server quote's number if the customer has fetched one; that's authoritative.
  const deliveryFee = quote?.delivery_fee != null
    ? Number(quote.delivery_fee)
    : (shippingApplies && selectedSize
        ? Math.round(baseDeliveryFee * shippingMultiplier * 100) / 100
        : baseDeliveryFee);
  const serviceFee = cart.items.length ? Math.max(1, cartTotal * 0.05) : 0;
  const taxAmount = taxRule ? Math.round(cartTotal * (taxRule.rate_pct / 100) * 100) / 100 : 0;
  // Use the backend-computed `discount_local` (already in the vendor's currency, FX-converted).
  // Fallback to a local approximation only if the field is missing on older responses.
  const discountLocal = appliedPromo
    ? (appliedPromo.discount_local != null
        ? Number(appliedPromo.discount_local)
        : (appliedPromo.discount_type === 'percent'
            ? (appliedPromo.target === 'delivery' ? deliveryFee : cartTotal) * (appliedPromo.discount_value / 100)
            : Number(appliedPromo.discount_value)))
    : 0;
  const vendorCurrency = appliedPromo?.currency || null;
  const grand = Math.max(0, (isMarketplaceDirect ? 0 : cartTotal + taxAmount) + deliveryFee + serviceFee - discountLocal);
  // Wallet is applied AFTER the standard total; the customer only owes the
  // remaining amount on their card. Capped at the actual delivery fee.
  const walletAppliedPreview = !isPickup && useWallet && walletBalance > 0
    ? Math.min(walletBalance, deliveryFee)
    : 0;
  const grandAfterWallet = Math.max(0, grand - walletAppliedPreview);

  const applyPromoCode = async (codeArg) => {
    const code = (codeArg || promoCode).trim();
    if (!code) return;
    setPromoLoading(true);
    try {
      const r = await api.post('/promotions/apply', {
        code,
        vendor_id: cart.vendorId,
        subtotal_cad: cartTotal,
        delivery_fee_cad: deliveryFee,
        item_ids: cart.items.map((i) => i.id),
      }).then((res) => res.data);
      setAppliedPromo(r);
      setPromoCode(code);
      toast.success(t('checkout.promo_applied', 'Promo applied'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('checkout.promo_invalid', 'Invalid promo code'));
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };
  const applyPromo = () => applyPromoCode();

  const pickCurrentLocation = () => {
    if (!('geolocation' in navigator)) {
      toast.error('This browser does not support location access.');
      return;
    }
    setUsingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude: lat, longitude: lng } = pos.coords;
          // Reverse-geocode via Nominatim (same source the app uses elsewhere)
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`;
          const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
          const data = res.ok ? await res.json() : null;
          const displayName = data?.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
          setAddress(displayName);
          setDestLat(lat);
          setDestLng(lng);
          setPickedAddressId(null);
          setUsedLocation(true);
          setSavedFromLocation(false);
          toast.success('Delivery address set from your current location');
        } catch (_e) {
          toast.error('Could not read your location — try typing your address instead.');
        } finally { setUsingLocation(false); }
      },
      (err) => {
        setUsingLocation(false);
        toast.error(err?.code === 1
          ? 'Location permission denied — try again after allowing access in your browser.'
          : 'Could not get your location. Type your address instead.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  };

  const clearPromo = () => { setAppliedPromo(null); setPromoCode(''); };

  if (cart.items.length === 0 && !placing) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header showSearch={false} />
        <div className="max-w-xl mx-auto p-10 text-center text-slate-500">
          {t('cart.empty', 'Your cart is empty.')}
          <div><Button className="mt-4 bg-emerald-600 hover:bg-emerald-700" onClick={() => nav('/')}>{t('common.find_stores', 'Find stores')}</Button></div>
        </div>
      </div>
    );
  }

  const fetchQuote = async () => {
    if (!isLogisticsVendor) return;
    if (pickupLat == null || pickupLng == null) {
      toast.error('Enter a pickup address first');
      return;
    }
    if (destLat == null || destLng == null) {
      toast.error('Enter a delivery address first');
      return;
    }
    setQuoting(true);
    try {
      const { data } = await api.post('/orders/quote', {
        vendor_id: cart.vendorId,
        pickup_lat: pickupLat,
        pickup_lng: pickupLng,
        pickup_address: pickupAddress || undefined,
        dest_lat: destLat,
        dest_lng: destLng,
        shipping_size_id: shippingSizeId || undefined,
        priority,
      });
      setQuote(data);
      toast.success(`Quote: ${format(data.delivery_fee)} · ${data.distance_km} km`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not compute quote');
    } finally {
      setQuoting(false);
    }
  };

  const placeOrderAction = async () => {
    // Guard: for delivery orders, we require real geocoded coordinates so drivers
    // and distance/pricing engines have something to work with.
    if (!isPickup && (destLat == null || destLng == null)) {
      toast.error('Please pick your delivery address from the suggestions so we can route the driver there.');
      return;
    }
    if (isLogisticsVendor && !isPickup && (pickupLat == null || pickupLng == null)) {
      toast.error('Please pick your pickup address from the suggestions so we can route the driver there.');
      return;
    }
    // Gate: e-transfer proof (reference + screenshot) is captured inline on the
    // payment card BEFORE placing the order — asked ONCE per invoice, never again.
    if (payForMe && !isPickup) {
      if (payForMeMethod === 'etransfer' && !fulfillEtRef.trim()) {
        toast.error('Add the e-transfer reference for your PAY FOR ME payment to ShipitNow'); return;
      }
    } else {
      if (!isMarketplaceDirect && goodsPayMethod === 'etransfer' && !goodsEtRef.trim()) {
        toast.error('Add the e-transfer reference for the vendor payment (green section)'); return;
      }
      if (!isPickup && fulfillPayMethod === 'etransfer' && !fulfillEtRef.trim()) {
        toast.error('Add the e-transfer reference for the ShipitNow payment (blue section)'); return;
      }
    }
    setPlacing(true);
    try {
      const order = await placeOrder({
        vendor_id: cart.vendorId,
        items: cart.items.map((i) => ({ id: i.id, name: i.name, price: i.price, qty: i.qty, image: i.image })),
        address: isPickup ? (vendor?.address || 'In-store pickup') : address,
        dest_lat: isPickup ? null : destLat,
        dest_lng: isPickup ? null : destLng,
        is_out_of_area: isPickup ? false : outOfArea,
        note,
        // Legacy single-method field — derived from the per-invoice pickers so
        // the customer is never asked for a payment method twice.
        payment: (payForMe && !isPickup) ? payForMeMethod : (goodsPayMethod === 'etransfer' || fulfillPayMethod === 'etransfer' ? 'etransfer' : goodsPayMethod),
        currency: currency.code,
        promo_code: appliedPromo?.code || undefined,
        preferred_driver_id: isPickup ? undefined : (preferredDriverId || undefined),
        driver_preference: isPickup ? undefined : driverPreference,
        fulfillment_type: fulfillmentType,
        shipping_size_id: shippingApplies ? (shippingSizeId || undefined) : undefined,
        pickup_lat: isLogisticsVendor && pickupLat != null ? pickupLat : undefined,
        pickup_lng: isLogisticsVendor && pickupLng != null ? pickupLng : undefined,
        pickup_address: isLogisticsVendor && pickupAddress ? pickupAddress : undefined,
        priority: !isPickup && priority ? true : undefined,
        combine_order: !isPickup && combineOptIn && combineCandidates?.count > 0 ? true : undefined,
        use_wallet: !isPickup && !payForMe && useWallet && fulfillPayMethod !== 'wallet' && walletBalance > 0 ? true : undefined,
        use_vendor_gift: !payForMe && (useVendorGift || goodsPayMethod === 'vendor_gift') && vendorGiftBalance > 0 ? true : undefined,
        // Auto-request instant confirmation when the customer picked wallet or
        // gift card for the fulfillment invoice. Backend still validates
        // wallet balance server-side.
        platform_pay_method: (
          !payForMe
          && (fulfillPayMethod === 'wallet' || fulfillPayMethod === 'giftcard')
          && !isPickup
        ) ? fulfillPayMethod : undefined,
        // Record the customer's chosen method for each invoice — used later
        // by vendor + admin confirmation UIs and by receipts.
        goods_pay_method: (payForMe && !isPickup) ? 'pay_for_me' : (isMarketplaceDirect ? 'direct_to_seller' : goodsPayMethod),
        fulfill_pay_method: (payForMe && !isPickup) ? 'pay_for_me' : fulfillPayMethod,
        pay_for_me: (payForMe && !isPickup) ? true : undefined,
        goods_settled_offline: isMarketplaceDirect || undefined,
        dropoff_preference: isPickup ? undefined : dropoffPref,
      });
      // Auto-submit e-transfer proofs for each invoice that the customer
      // chose to pay by e-transfer. Uploads the screenshot to
      // `etransfers/` (sensitive prefix) then registers the submission for
      // vendor / admin review. Errors are surfaced as toasts but never block
      // the order flow — the customer can always re-submit from TrackOrder.
      const uploadEtransfer = async (kind, ref, fileObj) => {
        if (!fileObj) return;
        try {
          const fd = new FormData();
          fd.append('file', fileObj);
          fd.append('prefix', 'etransfers');
          const up = await api.post('/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const relPath = up.data?.rel_path;
          if (!relPath) throw new Error('Upload failed');
          await api.post(`/orders/${order.order_id || order.id}/etransfer/submit`, {
            invoice_kind: kind, reference: ref, screenshot_rel_path: relPath,
          });
        } catch (e) {
          toast.error(`Couldn\u2019t attach ${kind === 'goods' ? 'vendor' : 'ShipitNow'} e-transfer proof — you can retry on the tracking page.`);
        }
      };
      if (payForMe && !isPickup) {
        if (payForMeMethod === 'etransfer' && fulfillEtFile) await uploadEtransfer('platform', fulfillEtRef, fulfillEtFile);
      } else {
        if (goodsPayMethod === 'etransfer' && goodsEtFile) {
          await uploadEtransfer('goods', goodsEtRef, goodsEtFile);
        }
        if (fulfillPayMethod === 'etransfer' && fulfillEtFile) {
          await uploadEtransfer('platform', fulfillEtRef, fulfillEtFile);
        }
      }
      // Persist the chosen address back to the global context for next time
      setDelivery({ address, lat: destLat, lng: destLng });
      // Only when BOTH invoices are card do we hand off to a single Stripe
      // Checkout session (one payment, both invoices auto-confirmed server-side
      // — no repeated confirmation asks).
      const goCardCheckout = isMarketplaceDirect
        ? fulfillPayMethod === 'card'
        : (payForMe && !isPickup)
          ? payForMeMethod === 'card'
          : (!payForMe && goodsPayMethod === 'card' && (isPickup || fulfillPayMethod === 'card'));
      // Snapshot the basket before clearing so a failed/cancelled card payment
      // can restore it (expires after 48h).
      if (goCardCheckout) backupCart(order.id);
      clearCart();
      if (goCardCheckout) {
        // Redirect to Stripe Checkout
        try {
          const { url } = await paymentsApi.checkout(order.id);
          window.location.href = url;
          return; // browser navigation; don't reset placing state
        } catch (err) {
          const detail = err?.response?.data?.detail || 'Stripe is not configured. Falling back to mock order.';
          toast.error(detail);
          // Fall through to tracking page even though payment didn't go through
          nav(`/track/${order.id}`);
          return;
        }
      }
      const anyEtx = payForMe || goodsPayMethod === 'etransfer' || fulfillPayMethod === 'etransfer';
      toast.success(payForMe
        ? (payForMeMethod === 'card'
          ? 'Order placed — card payment pending'
          : 'Order placed — ShipitNow will confirm your PAY FOR ME payment shortly')
        : anyEtx
          ? 'Order placed — your e-transfer details were sent for review'
          : 'Order placed!');
      nav(`/track/${order.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to place order';
      toast.error(msg);
      setPlacing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header showSearch={false} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => nav(-1)} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('common.back', 'Back')}
        </button>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t('checkout.title', 'Checkout')}</h1>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            {/* Fulfillment toggle: Delivery vs Pickup */}
            <section className="bg-white border border-slate-200 rounded-2xl p-3" data-testid="fulfillment-toggle">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFulfillmentType('delivery')}
                  data-testid="fulfillment-delivery"
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                    !isPickup ? 'border-emerald-600 bg-emerald-50/60 ring-1 ring-emerald-300'
                              : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${!isPickup ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
                    <Truck className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">{t('checkout.delivery_option', 'Delivery')}</div>
                    <div className="text-xs text-slate-500">{t('checkout.delivery_sub', 'Driver brings it to you')}</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFulfillmentType('pickup')}
                  data-testid="fulfillment-pickup"
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                    isPickup ? 'border-emerald-600 bg-emerald-50/60 ring-1 ring-emerald-300'
                             : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${isPickup ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
                    <Store className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">{t('checkout.pickup_option', 'Pickup')}</div>
                    <div className="text-xs text-slate-500">{t('checkout.pickup_sub', 'Collect at the store · no delivery fee')}</div>
                  </div>
                </button>
              </div>
            </section>

            {shippingApplies && (
              <div>
                <ShippingSizePicker
                  sizes={shippingCfg?.sizes || []}
                  value={effectiveSizeId}
                  onChange={(id) => { manualSizeRef.current = true; setAiSizeSource(null); setShippingSizeId(id); }}
                  baseFee={baseDeliveryFee}
                  formatMoney={format}
                  loading={false}
                />
                {aiSizeSource && !manualSizeRef.current && (
                  <p className="text-[11px] text-indigo-600 mt-1.5" data-testid="ai-size-note">
                    {aiSizeSource === 'ai' ? 'Box size picked by AI from your items' : 'Box size auto-picked from your basket'} — tap another size to change it.
                  </p>
                )}
              </div>
            )}

            {isLogisticsVendor && !isPickup && (
              <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="logistics-pickup-card">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="h-5 w-5 text-emerald-600" />
                  <h2 className="font-semibold text-slate-900">Pickup address</h2>
                </div>
                <p className="text-sm text-slate-500 mb-3">
                  Tell the driver where to collect your package. We&apos;ll compute distance to your delivery address.
                </p>
                <AddressAutocomplete
                  value={pickupAddress}
                  onChange={setPickupAddress}
                  onSelect={(v) => {
                    setPickupAddress(v.address || '');
                    setPickupLat(v.lat ?? null);
                    setPickupLng(v.lng ?? null);
                    setQuote(null);
                  }}
                  placeholder="Where should we pick up from?"
                  data-testid="pickup-address-input"
                />
                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                  <label
                    className="flex items-center gap-2 text-sm cursor-pointer select-none"
                    data-testid="priority-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={priority}
                      onChange={(e) => { setPriority(e.target.checked); setQuote(null); }}
                      className="h-4 w-4 accent-emerald-600"
                    />
                    <span className="text-slate-700">
                      Priority delivery
                      {categoryCfg?.priority_delivery_multiplier > 1 && (
                        <span className="text-slate-400 ml-1">
                          (×{Number(categoryCfg.priority_delivery_multiplier).toFixed(1)})
                        </span>
                      )}
                    </span>
                  </label>
                  <Button
                    onClick={fetchQuote}
                    disabled={quoting || !pickupLat || destLat == null}
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="get-quote-btn"
                  >
                    {quoting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                    {quote ? 'Refresh quote' : 'Get quote'}
                  </Button>
                </div>
                {quote && (
                  <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900" data-testid="quote-result">
                    Estimated fee: <b>{format(quote.delivery_fee)}</b>
                    {' '}for <b>{quote.distance_km} km</b>
                    {quote.shipping_size && <> · {quote.shipping_size.name} (×{Number(quote.shipping_size_multiplier).toFixed(1)})</>}
                    {quote.priority && <> · priority</>}
                  </div>
                )}
              </section>
            )}

            {launchActive && selectedSize && !isLogisticsVendor && (
              <section className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-sm text-amber-900" data-testid="launch-discount-banner">
                🎉 Launch offer: {selectedSize.name} shipping is included on all {launchDiscount.label || vendorCategory} orders until{' '}
                {launchDiscount.discount_expires_at
                  ? new Date(launchDiscount.discount_expires_at).toLocaleDateString()
                  : 'further notice'}.
              </section>
            )}

            {isPickup ? (
              <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pickup-address-card">
                <div className="flex items-center gap-2 mb-3">
                  <Store className="h-5 w-5 text-emerald-600" />
                  <h2 className="font-semibold text-slate-900">{t('checkout.pickup_address', 'Pickup at the store')}</h2>
                </div>
                <div className="text-sm text-slate-700 font-medium">{vendor?.name || cart.vendorName}</div>
                {vendor?.address && (
                  <div className="mt-1 text-sm text-slate-500 flex items-start gap-1.5">
                    <MapPin className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
                    <span>{vendor.address}</span>
                  </div>
                )}
                <p className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                  {t('checkout.pickup_qr_note', "We'll generate a pickup QR for your order. Show it at the store to collect.")}
                </p>
                <Label className="mt-4 block text-slate-700">{t('checkout.note', 'Pickup notes (optional)')}</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('checkout.pickup_note_placeholder', 'Anything the store should know (e.g. allergies)')} className="mt-1.5" />
              </section>
            ) : (
              <section className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="h-5 w-5 text-emerald-600" />
                  <h2 className="font-semibold text-slate-900">{t('checkout.address', 'Delivery address')}</h2>
                </div>

                {/* 3-way delivery address chooser: enable location · use current · saved */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4" data-testid="delivery-address-chooser">
                  <button
                    type="button"
                    onClick={() => pickCurrentLocation()}
                    disabled={usingLocation}
                    className={`inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl text-sm font-semibold border transition-all ${usedLocation ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50'} disabled:opacity-60`}
                    data-testid="use-current-location-btn"
                  >
                    {usingLocation
                      ? <><Loader2 className="h-4 w-4 animate-spin" /> Enabling…</>
                      : usedLocation
                        ? <><CheckCircle2 className="h-4 w-4" /> Location on</>
                        : <><Navigation className="h-4 w-4" /> Enable & use my location</>}
                  </button>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1.5 px-2">
                    {usedLocation
                      ? 'Address auto-filled from GPS — tweak below if needed.'
                      : 'One-tap: your browser asks for permission, then fills the address below.'}
                  </div>
                </div>

                <SavedAddressPicker
                  selectedId={pickedAddressId}
                  onPick={(a) => {
                    setAddress(a.address);
                    setDestLat(a.lat);
                    setDestLng(a.lng);
                    setPickedAddressId(a.id);
                    setUsedLocation(false);
                    setSavedFromLocation(false);
                  }}
                  onClear={() => {
                    setAddress('');
                    setDestLat(null);
                    setDestLng(null);
                    setPickedAddressId(null);
                    setUsedLocation(false);
                    setSavedFromLocation(false);
                  }}
                />
                {usedLocation && !pickedAddressId && destLat != null && destLng != null && !savedFromLocation && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const addr = await addressesApi.add({
                          label: 'Home', address, lat: destLat, lng: destLng, is_default: false,
                        });
                        setSavedFromLocation(true);
                        setPickedAddressId(addr.id);
                        toast.success('Saved as Home — it will appear in Saved addresses next time.');
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Could not save address');
                      }
                    }}
                    className="mt-3 w-full sm:w-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm"
                    data-testid="save-as-home-btn"
                  >
                    <Heart className="h-3.5 w-3.5" /> Save this as home
                  </button>
                )}
                {savedFromLocation && (
                  <div className="mt-3 text-xs text-emerald-700 font-semibold flex items-center gap-1" data-testid="saved-as-home-ack">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Saved as Home
                  </div>
                )}
                <div className={pickedAddressId ? 'mt-3 opacity-80' : 'mt-3'}>
                  <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    {pickedAddressId ? 'Or type a new address' : 'Enter delivery address'}
                  </div>
                  <AddressAutocomplete
                    value={address}
                    onChange={(addr, geo) => {
                      setAddress(addr); setDestLat(geo?.lat ?? null); setDestLng(geo?.lng ?? null);
                      if (pickedAddressId) setPickedAddressId(null);
                    }}
                    placeholder={t('checkout.address_placeholder', 'Where should we deliver?')} />
                </div>
                {address && (destLat == null || destLng == null) ? (
                  <p className="mt-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 inline-block" data-testid="address-not-geocoded-warning">
                    Pick a suggestion from the dropdown so we get precise coordinates. Free-typed addresses can&rsquo;t be delivered to.
                  </p>
                ) : destLat != null && destLng != null ? (
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <p className="text-[11px] text-emerald-700" data-testid="address-geocoded-ok">
                      Address confirmed. We&rsquo;ll route drivers to this exact spot.
                    </p>
                    {!pickedAddressId && user && (
                      <SavedAddressSaveButton address={address} lat={destLat} lng={destLng} defaultLabel="Home" />
                    )}
                  </div>
                ) : null}
                <label className="mt-3 flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                  <input type="checkbox" checked={outOfArea} onChange={(e) => setOutOfArea(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" data-testid="out-of-area-checkbox" />
                  <span>{t('checkout.out_of_area_check', 'This is an out-of-area order (more than 25 km from the store)')}</span>
                  {outOfArea && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                </label>

                {/* Drop-off preference — 'meet at door' skips the driver's POD photos */}
                <div className="mt-4">
                  <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Drop-off preference</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setDropoffPref('leave_at_door')}
                      className={`rounded-xl border-2 p-3 text-left transition ${dropoffPref === 'leave_at_door' ? 'border-emerald-500 bg-emerald-50/60' : 'border-slate-200 hover:border-slate-300'}`}
                      data-testid="dropoff-leave-at-door"
                    >
                      <div className="text-sm font-medium text-slate-900">Leave at door</div>
                      <p className="text-[11px] text-slate-500 mt-0.5">Driver takes 2 photos as proof of delivery.</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDropoffPref('meet_at_door')}
                      className={`rounded-xl border-2 p-3 text-left transition ${dropoffPref === 'meet_at_door' ? 'border-emerald-500 bg-emerald-50/60' : 'border-slate-200 hover:border-slate-300'}`}
                      data-testid="dropoff-meet-at-door"
                    >
                      <div className="text-sm font-medium text-slate-900">Meet driver at door</div>
                      <p className="text-[11px] text-slate-500 mt-0.5">Hand-to-hand — no delivery photos needed.</p>
                    </button>
                  </div>
                </div>
                <Label className="mt-4 block text-slate-700">{t('checkout.note', 'Delivery notes (optional)')}</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('checkout.note_placeholder', 'Apartment number, gate code, etc.')} className="mt-1.5" />
              </section>
            )}

            {!isPickup && (
              <DriverPreferenceSelector value={driverPreference} onChange={setDriverPreference} />
            )}

            <section className="bg-white border border-slate-200 rounded-2xl p-5">
              <h2 className="font-semibold text-slate-900 mb-3">{t('cart.items_from', 'Items from {{vendor}}', { vendor: cart.vendorName })}</h2>
              <ul className="divide-y divide-slate-100">
                {cart.items.map((i) => (
                  <li key={i.id} className="py-2.5 flex items-center justify-between text-sm">
                    <span className="text-slate-700"><b className="text-slate-900">{i.qty}×</b> {i.name}</span>
                    <span className="font-medium text-slate-900">{format(i.price * i.qty)}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <aside className="bg-white border border-slate-200 rounded-2xl p-5 h-fit sticky top-20">
            <h3 className="font-semibold text-slate-900">{t('checkout.summary', 'Order summary')}</h3>

            <div className="mt-4">
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5 text-emerald-600" /> {t('checkout.promo_code', 'Promo code')}
              </Label>
              {appliedPromo ? (
                <div className="mt-1.5 flex items-center justify-between gap-2 px-3 h-10 rounded-md bg-emerald-50 border border-emerald-200" data-testid="applied-promo">
                  <div className="text-sm text-emerald-800 font-medium truncate">
                    {appliedPromo.code}
                    <span className="text-emerald-600 ml-1.5 text-xs">
                      ({appliedPromo.discount_type === 'percent' ? `${appliedPromo.discount_value}%` : `CAD ${appliedPromo.discount_value}`} off {appliedPromo.target})
                    </span>
                  </div>
                  <button onClick={clearPromo} className="text-emerald-700 hover:text-emerald-900" data-testid="clear-promo-btn">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div>
                  <div className="mt-1.5 flex gap-2">
                    <Input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder={t('checkout.promo_placeholder', 'Enter code')} className="h-10" data-testid="promo-code-input" />
                    <Button onClick={applyPromo} disabled={promoLoading || !promoCode.trim()} variant="outline" className="h-10" data-testid="apply-promo-btn">
                      {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.apply', 'Apply')}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 space-y-2 text-sm">
              <Row label={t('checkout.subtotal', 'Subtotal')} value={format(cartTotal)} />
              {!isPickup && (
                <Row
                  label={selectedSize
                    ? `${t('checkout.delivery_fee', 'Delivery fee')} · ${selectedSize.name} (×${shippingMultiplier.toFixed(1)})`
                    : t('checkout.delivery_fee', 'Delivery fee')}
                  value={format(deliveryFee)}
                />
              )}
              {!isPickup && quote && (
                <DeliveryFeeBreakdown
                  breakdown={quote.breakdown}
                  distanceKm={quote.distance_km}
                  currency={vendor?.currency || cart?.vendorCurrency}
                  total={quote.delivery_fee}
                />
              )}
              <Row label={t('checkout.service_fee', 'Service fee')} value={format(serviceFee)} />
              {taxAmount > 0 && (
                <Row label={`${t('checkout.tax', 'Tax')} (${taxRule.label} ${taxRule.rate_pct}%)`} value={format(taxAmount)} />
              )}
              {discountLocal > 0 && (
                <Row label={`${t('checkout.discount', 'Discount')} (${appliedPromo.code})`}
                     value={`- ${format(discountLocal)}${vendorCurrency && currency?.code && vendorCurrency !== currency.code ? ` (${vendorCurrency})` : ''}`}
                     emerald />
              )}
              <div className="h-px bg-slate-200 my-3" />
              <Row label={t('checkout.total', 'Total')} value={format(grand)} bold />
              {walletAppliedPreview > 0 && (
                <>
                  <Row label="Wallet applied" value={`− ${format(walletAppliedPreview)}`} />
                  <Row label={t('checkout.total_due', 'Total due today')} value={format(grandAfterWallet)} bold />
                </>
              )}
            </div>

            {/* -------------------------------------------------------------
                Two clearly separated payment sections, each with its own
                inline method picker. Vendor invoice = full COGS the customer
                paid (no addition, no subtraction). Fulfillment = delivery +
                service, paid to ShipitNow.
             ------------------------------------------------------------- */}
            {!isPickup && !isMarketplaceDirect && (
              <button
                type="button"
                onClick={() => setPayForMe((v) => !v)}
                className={`mt-5 w-full text-left rounded-xl border-2 p-4 transition-colors ${payForMe ? 'border-indigo-600 bg-indigo-50/60' : 'border-slate-200 bg-white hover:border-indigo-300'}`}
                data-testid="pay-for-me-toggle"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-bold">Pay for me</div>
                    <div className="text-sm font-semibold text-slate-900 mt-0.5">One payment to ShipitNow — we settle the store for you</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Pay goods + delivery + service in ONE payment to ShipitNow — by card or Interac e-transfer. Once confirmed,
                      the store starts preparing and a ShipitNow Super Driver pays the store in person at pickup. No QR scanning.
                    </div>
                  </div>
                  <div className={`h-5 w-5 shrink-0 rounded-full border-2 mt-1 ${payForMe ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'}`} />
                </div>
              </button>
            )}

            {payForMe && !isPickup ? (
              <div className="mt-3 rounded-xl border-2 border-indigo-600 bg-indigo-50/40 p-4" data-testid="pay-for-me-section">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-bold">Single payment · Everything</div>
                    <div className="text-sm font-semibold text-slate-900 mt-0.5">Paid to ShipitNow</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Goods + tax + delivery + service. ShipitNow admin confirms, then the store is notified to prepare.</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Amount</div>
                    <div className="text-lg font-bold text-slate-900" data-testid="pay-for-me-amount">{format(grand)}</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-indigo-300">
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setPayForMeMethod('card')}
                      className={`h-10 rounded-lg border text-sm font-medium ${payForMeMethod === 'card' ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-indigo-400'}`}
                      data-testid="pay-for-me-method-card"
                    >
                      Pay by card
                    </button>
                    <button
                      type="button"
                      onClick={() => setPayForMeMethod('etransfer')}
                      className={`h-10 rounded-lg border text-sm font-medium ${payForMeMethod === 'etransfer' ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-indigo-400'}`}
                      data-testid="pay-for-me-method-etransfer"
                    >
                      Interac e-transfer
                    </button>
                  </div>
                  {payForMeMethod === 'card' ? (
                    <p className="text-[11px] text-slate-500" data-testid="pay-for-me-card-note">
                      You&rsquo;ll be redirected to a secure card checkout for {format(grand)} after placing the order.
                      ShipitNow receives the payment instantly and notifies the store to start preparing.
                    </p>
                  ) : (
                    <ETransferInlineCapture
                      testidKind="payforme"
                      reference={fulfillEtRef}
                      setReference={setFulfillEtRef}
                      file={fulfillEtFile}
                      setFile={setFulfillEtFile}
                      recipientHint={interacEmails.platform ? 'Send the FULL amount to ShipitNow at the Interac address below.' : 'ShipitNow admin will share Interac details on the tracking page.'}
                      recipientEmail={interacEmails.platform}
                    />
                  )}
                </div>
              </div>
            ) : (
            <div className="mt-5 space-y-3" data-testid="dual-payment-sections">
              {isMarketplaceDirect ? (
                <div className="rounded-xl border-2 border-amber-400 bg-amber-50/50 p-4" data-testid="marketplace-direct-section">
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Payment 1 · Item</div>
                  <div className="text-sm font-semibold text-slate-900 mt-0.5">Paid directly to the seller</div>
                  <p className="text-[11px] text-slate-600 mt-1">
                    You arranged to pay <b>{cart?.vendorName || 'the seller'}</b> yourselves (cash, e-transfer, etc.).
                    ShipitNow only charges the delivery + service fees below — a driver picks up from the seller and delivers to you.
                  </p>
                  <div className="mt-2 text-right">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-2">Item amount (settled offline)</span>
                    <span className="text-lg font-bold text-slate-900" data-testid="marketplace-direct-amount">{format(cartTotal)}</span>
                  </div>
                </div>
              ) : (
              <div className="rounded-xl border-2 border-emerald-600 bg-emerald-50/40 p-4" data-testid="cogs-payment-section">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Payment 1 · Goods</div>
                    <div className="text-sm font-semibold text-slate-900 mt-0.5">Paid to {cart?.vendorName || 'the vendor'}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Vendor manually confirms receipt in their portal.</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Amount</div>
                    <div className="text-lg font-bold text-slate-900" data-testid="cogs-amount">{format(cartTotal + taxAmount)}</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-emerald-300">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Pay this by</div>
                  <div className="grid grid-cols-2 gap-2" data-testid="cogs-method-picker">
                    <MethodPill selected={goodsPayMethod === 'card'} onClick={() => setGoodsPayMethod('card')} icon={CreditCard} label="Card" hint="Direct to vendor" data-testid="cogs-method-card" />
                    <MethodPill selected={goodsPayMethod === 'etransfer'} onClick={() => setGoodsPayMethod('etransfer')} icon={Wallet} label="E-transfer" hint="Upload receipt" data-testid="cogs-method-etransfer" />
                    {useVendorGift && vendorGiftBalance > 0 && (
                      <MethodPill selected={goodsPayMethod === 'vendor_gift'} onClick={() => setGoodsPayMethod('vendor_gift')} icon={Wallet} label="Store credit" hint={`CA$${vendorGiftBalance.toFixed(2)} available`} data-testid="cogs-method-vendor-gift" />
                    )}
                  </div>
                  {goodsPayMethod === 'etransfer' && (
                    <ETransferInlineCapture
                      testidKind="cogs"
                      reference={goodsEtRef}
                      setReference={setGoodsEtRef}
                      file={goodsEtFile}
                      setFile={setGoodsEtFile}
                      recipientHint={interacEmails.vendor ? `Send to ${cart?.vendorName || 'the vendor'} at the Interac address below.` : `Send to ${cart?.vendorName || 'the vendor'} — ask them for their Interac details.`}
                      recipientEmail={interacEmails.vendor}
                    />
                  )}
                </div>
              </div>
              )}

              <div className="rounded-xl border-2 border-blue-800 bg-blue-50/40 p-4" data-testid="fulfillment-payment-section">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-blue-800 font-bold">Payment 2 · Fulfillment</div>
                    <div className="text-sm font-semibold text-slate-900 mt-0.5">Paid to ShipitNow</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Delivery + service. Card &amp; e-transfer confirm manually; wallet &amp; gift card confirm instantly.</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Amount</div>
                    <div className="text-lg font-bold text-slate-900" data-testid="fulfillment-amount">{format((isPickup ? 0 : deliveryFee) + serviceFee)}</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-blue-300">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Pay this by</div>
                  <div className="grid grid-cols-2 gap-2" data-testid="fulfillment-method-picker">
                    <MethodPill selected={fulfillPayMethod === 'card'} onClick={() => setFulfillPayMethod('card')} icon={CreditCard} label="Card" hint="Admin confirms" data-testid="fulfillment-method-card" />
                    <MethodPill
                      selected={fulfillPayMethod === 'wallet'}
                      onClick={() => setFulfillPayMethod('wallet')}
                      icon={Wallet}
                      label="Wallet"
                      hint={walletBalance > 0 ? `CA$${walletBalance.toFixed(2)} · instant` : 'Instant'}
                      disabled={walletBalance < ((isPickup ? 0 : deliveryFee) + serviceFee)}
                      data-testid="fulfillment-method-wallet"
                    />
                    <MethodPill selected={fulfillPayMethod === 'etransfer'} onClick={() => setFulfillPayMethod('etransfer')} icon={Wallet} label="E-transfer" hint="Upload receipt" data-testid="fulfillment-method-etransfer" />
                    <MethodPill selected={fulfillPayMethod === 'giftcard'} onClick={() => setFulfillPayMethod('giftcard')} icon={Wallet} label="Gift card" hint="Instant" data-testid="fulfillment-method-giftcard" />
                  </div>
                  {fulfillPayMethod === 'etransfer' && (
                    <ETransferInlineCapture
                      testidKind="fulfillment"
                      reference={fulfillEtRef}
                      setReference={setFulfillEtRef}
                      file={fulfillEtFile}
                      setFile={setFulfillEtFile}
                      recipientHint={interacEmails.platform ? 'Send to ShipitNow at the Interac address below.' : 'ShipitNow admin will share Interac details on the tracking page.'}
                      recipientEmail={interacEmails.platform}
                    />
                  )}
                </div>
              </div>
            </div>
            )}

            {!payForMe && !isPickup && walletBalance > 0 && deliveryFee > 0 && (
              <div
                className="mt-5 rounded-xl border border-emerald-300 bg-white p-3.5 flex items-start gap-3"
                data-testid="wallet-checkout-card"
              >
                <div className="h-9 w-9 shrink-0 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <Wallet className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">
                    Use ShipitNow wallet · ${walletBalance.toFixed(2)} available
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Applies up to <b>${Math.min(walletBalance, deliveryFee).toFixed(2)}</b> toward the delivery fee. Item subtotal still charges your card.
                  </div>
                  <label className="mt-2 flex items-center gap-2 text-[12px] text-slate-900 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      checked={useWallet}
                      onChange={(e) => setUseWallet(e.target.checked)}
                      data-testid="wallet-use-toggle"
                    />
                    Pay delivery from my wallet
                  </label>
                </div>
              </div>
            )}

            {vendorGiftBalance > 0 && cartTotal > 0 && (
              <div
                className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3.5 flex items-start gap-3"
                data-testid="vendor-gift-checkout-card"
              >
                <div className="h-9 w-9 shrink-0 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
                  <span className="text-lg">🎁</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">
                    Store credit available · CA${vendorGiftBalance.toFixed(2)}
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    <b>{cart?.vendorName || 'This store'}</b> has pre-funded a gift pool. Redeem up to{' '}
                    <b>CA${Math.min(vendorGiftBalance, cartTotal + taxAmount).toFixed(2)}</b> off the goods total.
                  </div>
                  <label className="mt-2 flex items-center gap-2 text-[12px] text-slate-900 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                      checked={useVendorGift}
                      onChange={(e) => setUseVendorGift(e.target.checked)}
                      data-testid="vendor-gift-use-toggle"
                    />
                    Redeem store gift credit
                  </label>
                </div>
              </div>
            )}

            {!isPickup && combineCandidates?.count > 0 && (
              <div
                className="mt-5 rounded-xl border border-emerald-300 bg-emerald-50/60 p-3.5"
                data-testid="combine-orders-card"
              >
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 shrink-0 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                    <Truck className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-emerald-900" data-testid="combine-orders-headline">
                      {combineCandidates.count} nearby {combineCandidates.count === 1 ? 'order' : 'orders'} at this store — save {combineCandidates.discount_pct}% on delivery
                    </div>
                    <div className="text-[11px] text-emerald-800/80 mt-0.5">
                      Closest is {combineCandidates.closest_km} km from you. Combining lets the driver batch pickups and drop-offs on the same trip.
                    </div>
                    <label className="mt-2 flex items-center gap-2 text-[12px] text-emerald-900 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-emerald-400 text-emerald-600 focus:ring-emerald-500"
                        checked={combineOptIn}
                        onChange={(e) => setCombineOptIn(e.target.checked)}
                        data-testid="combine-orders-optin"
                      />
                      Yes, combine my order with a nearby one
                    </label>
                  </div>
                </div>
              </div>
            )}

            {!isPickup && nearbyDrivers.length > 0 && (
              <div className="mt-5 rounded-xl border border-slate-200 p-3.5 bg-slate-50/40" data-testid="driver-picker">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                    <Bike className="h-4 w-4 text-emerald-600" /> Choose your driver
                    <span className="text-[11px] font-normal text-slate-500">(optional)</span>
                  </div>
                  {preferredDriverId && (
                    <button onClick={() => setPreferredDriverId(null)}
                      className="text-[11px] text-slate-500 hover:text-slate-800 underline" data-testid="clear-driver-btn">
                      Auto-assign instead
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">Pick a driver near the merchant — or leave unset and we&rsquo;ll auto-match the nearest.</p>
                <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1" data-testid="registered-drivers-only-note">
                  <Bike className="h-3 w-3" /> All ShipitNow deliveries and courier trips are handled by registered, background-checked drivers only.
                </p>
                <div className="mt-2.5 grid grid-cols-1 gap-2 max-h-56 overflow-y-auto pr-1">
                  {nearbyDrivers.map((d) => {
                    const active = preferredDriverId === d.driver_id;
                    return (
                      <button
                        key={d.driver_id}
                        type="button"
                        onClick={() => setPreferredDriverId(active ? null : d.driver_id)}
                        data-testid={`driver-pick-${d.driver_id}`}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border text-left transition-colors ${
                          active ? 'bg-emerald-50 border-emerald-300 ring-1 ring-emerald-300'
                                 : 'bg-white border-slate-200 hover:border-emerald-200'}`}
                      >
                        <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold text-sm shrink-0">
                          {(d.name || 'D').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                            {d.name}
                            {d.is_favorite && <Heart className="h-3 w-3 fill-rose-500 text-rose-500" />}
                          </div>
                          <div className="text-[11px] text-slate-500 flex items-center gap-2">
                            {d.rating != null && <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{d.rating?.toFixed?.(1) ?? d.rating}</span>}
                            <span>{d.distance_km} km</span>
                            {d.deliveries_done ? <span>· {d.deliveries_done} deliveries</span> : null}
                          </div>
                        </div>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(d.driver_id); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); toggleFavorite(d.driver_id); } }}
                          className={`p-1.5 rounded-full hover:bg-rose-50 cursor-pointer ${d.is_favorite ? 'text-rose-500' : 'text-slate-400 hover:text-rose-500'}`}
                          data-testid={`fav-toggle-${d.driver_id}`}
                          aria-label={d.is_favorite ? 'Remove favorite' : 'Mark as favorite'}
                        >
                          <Heart className={`h-4 w-4 ${d.is_favorite ? 'fill-rose-500' : ''}`} />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <Button
              onClick={placeOrderAction}
              disabled={placing || (!isPickup && (destLat == null || destLng == null))}
              data-testid="place-order-btn"
              className="mt-5 w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
            >
              {placing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              {placing ? t('checkout.placing', 'Placing order…') : `${t('checkout.place_order', 'Place order')} • ${format(grandAfterWallet)}`}
            </Button>
            {!isPickup && (destLat == null || destLng == null) && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2 text-center" data-testid="place-order-hint">
                {t('checkout.address_required_hint',
                  'Pick a saved address or type one above (choose from the dropdown) to unlock “Place order”.')}
              </p>
            )}
            <p className="text-xs text-slate-400 mt-3 text-center">{t('checkout.paying_as', 'Paying as {{email}}', { email: user?.email })}</p>
          </aside>
        </div>
      </main>
    </div>
  );
};

const Row = ({ label, value, bold, emerald }) => (
  <div className="flex items-center justify-between">
    <span className={bold ? 'text-slate-900 font-semibold' : (emerald ? 'text-emerald-700' : 'text-slate-600')}>{label}</span>
    <span className={bold ? 'text-slate-900 font-semibold' : (emerald ? 'text-emerald-700 font-medium' : 'text-slate-900')}>{value}</span>
  </div>
);

// Compact inline payment-method pill used inside the two payment cards.
// Renders as a bordered button with an icon, label, and short hint. Disabled
// pills gray out but still show the hint (so users know why it's unavailable).
const MethodPill = ({ selected, onClick, icon: Icon, label, hint, disabled, ...rest }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    className={`w-full text-left rounded-lg border-2 px-2.5 py-2 transition-colors ${
      selected
        ? 'border-emerald-600 bg-white shadow-sm'
        : disabled
          ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
          : 'border-slate-200 bg-white hover:border-slate-300'
    }`}
    {...rest}
  >
    <div className="flex items-center gap-1.5">
      <Icon className={`h-3.5 w-3.5 ${selected ? 'text-emerald-600' : 'text-slate-500'}`} />
      <span className={`text-[12px] font-semibold ${selected ? 'text-slate-900' : 'text-slate-700'}`}>{label}</span>
    </div>
    {hint && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{hint}</div>}
  </button>
);

// Inline e-transfer capture — reference number + screenshot upload.
// Used on both payment cards when the customer picks E-transfer. Values are
// posted to the etransfer submission endpoint right after order create.
const ETransferInlineCapture = ({ testidKind, reference, setReference, file, setFile, recipientHint, recipientEmail }) => {
  const [preview, setPreview] = React.useState(null);
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      window.alert('Screenshot too large (max 15 MB)');
      return;
    }
    setFile(f);
  };
  const copyEmail = async () => {
    if (!recipientEmail) return;
    try {
      await navigator.clipboard.writeText(recipientEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* clipboard blocked */ }
  };
  return (
    <div className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-white p-3" data-testid={`${testidKind}-etransfer-inline`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">E-transfer proof</div>
      <div className="text-[11px] text-slate-500">{recipientHint}</div>
      {/* Bank details — the exact Interac recipient email the customer must
          use. Rendered when the vendor / platform admin has configured one. */}
      {recipientEmail && (
        <div className="rounded-md border-2 border-emerald-500 bg-emerald-50 p-2 space-y-1" data-testid={`${testidKind}-interac-card`}>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Send Interac e-transfer to</div>
            <button
              type="button"
              onClick={copyEmail}
              className="text-[10px] text-emerald-800 underline underline-offset-2 font-medium"
              data-testid={`${testidKind}-interac-copy`}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <code className="block text-[13px] font-mono font-semibold text-slate-900 break-all" data-testid={`${testidKind}-interac-email`}>
            {recipientEmail}
          </code>
        </div>
      )}
      <input
        type="text"
        placeholder="Confirmation reference #"
        className="w-full text-sm rounded-md border border-slate-300 px-2.5 py-1.5 focus:border-emerald-600 focus:outline-none"
        value={reference}
        onChange={(e) => setReference(e.target.value)}
        data-testid={`${testidKind}-etransfer-ref-input`}
      />
      <label className="flex items-center justify-between gap-2 rounded-md border border-dashed border-slate-300 px-2.5 py-2 cursor-pointer hover:border-emerald-500" data-testid={`${testidKind}-etransfer-file-label`}>
        <span className="text-[12px] font-medium text-slate-700 truncate">
          {file ? file.name.slice(0, 30) : 'Attach screenshot'}
        </span>
        <input type="file" accept="image/*" className="hidden" onChange={onPick} data-testid={`${testidKind}-etransfer-file-input`} />
        <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
          {file ? 'Change' : 'Choose'}
        </span>
      </label>
      {preview && (
        <img
          src={preview}
          alt="E-transfer preview"
          className="w-full max-h-40 object-contain rounded-md bg-slate-50"
          data-testid={`${testidKind}-etransfer-preview`}
        />
      )}
    </div>
  );
};

export default Checkout;
